import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import clinicalNoteService from './clinicalNoteService.js';
import './ClinicalNoteSpecialTestCompact.css';

/**
 * Delay between the clinician's final edit and autosave.
 */
const AUTOSAVE_DELAY_MS = 900;

/**
 * Resolve ClinicalNoteMain.entityVersion from a note response.
 */
function resolveEntityVersion(response) {
  if (!response) {
    return null;
  }

  return (
    response.entityVersion ??
    response.note?.entityVersion ??
    response.clinicalNote?.entityVersion ??
    null
  );
}

/**
 * A special-test result is uniquely identified by:
 *
 * specialTestTemplateId + measurementSide
 */
function getSpecialTestKey(row) {
  return (
    `${row?.specialTestTemplateId ?? 'unknown'}|` +
    `${row?.measurementSide ?? 'NONE'}`
  );
}

/**
 * Convert nullable API text into a controlled React value.
 */
function toInputValue(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return '';
  }

  return String(value);
}

/**
 * Normalize one SpecialTestResponse for frontend editing.
 */
function normalizeSpecialTestRow(row) {
  return {
    ...row,

    draftTestResult:
      toInputValue(
        row?.testResult
      ),

    draftTestComments:
      toInputValue(
        row?.testComments
      ),
  };
}

/**
 * Convert backend laterality to clinician-facing text.
 */
function formatSide(side) {
  switch (side) {
    case 'LEFT':
      return 'Left';

    case 'RIGHT':
      return 'Right';

    case 'BILATERAL':
      return 'Bilateral';

    case 'NOT_APPLICABLE':
      return '';

    default:
      return side || '';
  }
}

/**
 * Sort by backend display order, then test name and side.
 */
function sortSpecialTestRows(rows) {
  return [...rows].sort(
    (first, second) => {
      const firstOrder =
        Number.isFinite(
          Number(
            first?.displayOrder
          )
        )
          ? Number(
              first.displayOrder
            )
          : Number.MAX_SAFE_INTEGER;

      const secondOrder =
        Number.isFinite(
          Number(
            second?.displayOrder
          )
        )
          ? Number(
              second.displayOrder
            )
          : Number.MAX_SAFE_INTEGER;

      if (
        firstOrder !==
        secondOrder
      ) {
        return (
          firstOrder -
          secondOrder
        );
      }

      const nameComparison =
        String(
          first?.displayName ??
            first?.testCode ??
            ''
        ).localeCompare(
          String(
            second?.displayName ??
              second?.testCode ??
              ''
          )
        );

      if (
        nameComparison !== 0
      ) {
        return nameComparison;
      }

      return String(
        first?.measurementSide ??
          ''
      ).localeCompare(
        String(
          second?.measurementSide ??
            ''
        )
      );
    }
  );
}

/**
 * Special-test text itself is free-form.
 *
 * Blank required values are still permitted while the note remains
 * in DRAFT. Full note validation determines whether review can begin.
 */
function validateSpecialTestRow() {
  return {};
}

/**
 * Merge one successful save response into the current row.
 *
 * If another edit happened while the request was in flight, preserve
 * that newer draft while refreshing backend information.
 */
function mergeSavedSpecialTestRow(
  currentRow,
  savedResponse,
  preserveDraft
) {
  const normalized =
    normalizeSpecialTestRow(
      savedResponse
    );

  if (!preserveDraft) {
    return normalized;
  }

  return {
    ...normalized,

    draftTestResult:
      currentRow
        .draftTestResult,

    draftTestComments:
      currentRow
        .draftTestComments,
  };
}

/**
 * Previous visit information for one special test.
 */
function PreviousSpecialTest({
  row,
}) {
  const hasPrevious =
    Boolean(
      row.previousTestResult ||
      row.previousTestComments
    );

  return (
    <div
      style={{
        border:
          '1px solid #e0e3e7',
        borderRadius: 7,
        background:
          '#fafafa',
        padding: 10,
      }}
    >
      <div
        style={{
          marginBottom: 5,
          fontSize: 11,
          fontWeight: 700,
          textTransform:
            'uppercase',
          color: '#777',
        }}
      >
        Previous Visit
      </div>

      {!hasPrevious && (
        <div
          style={{
            fontSize: 12,
            color: '#777',
          }}
        >
          No previous result is available
          for this test and side.
        </div>
      )}

      {hasPrevious && (
        <div
          style={{
            display: 'grid',
            gap: 8,
          }}
        >
          <div>
            <div
              style={{
                marginBottom: 2,
                fontSize: 11,
                fontWeight: 700,
                color: '#666',
              }}
            >
              Result
            </div>

            <div
              style={{
                fontSize: 13,
                whiteSpace:
                  'pre-wrap',
              }}
            >
              {row.previousTestResult ||
                '—'}
            </div>
          </div>

          {row.previousTestComments && (
            <div>
              <div
                style={{
                  marginBottom: 2,
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#666',
                }}
              >
                Comments
              </div>

              <div
                style={{
                  fontSize: 13,
                  whiteSpace:
                    'pre-wrap',
                }}
              >
                {row.previousTestComments}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One special-test editor card.
 */
function SpecialTestRow({
  row,
  disabled,
  saveStatus,
  saveError,
  onFieldChange,
  onRetry,
}) {
  const [showComments, setShowComments] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  const [largeResult, setLargeResult] = useState(false);
  const [largeComments, setLargeComments] = useState(false);

  const key =
    getSpecialTestKey(row);

  const side =
    formatSide(
      row.measurementSide
    );

  const requiredSatisfied =
    !row.required ||
    Boolean(
      row?.draftTestResult &&
      String(row.draftTestResult).trim()
    );

  let saveStatusText =
    disabled
      ? 'Read only'
      : 'Saved';

  switch (saveStatus) {
    case 'saving':
      saveStatusText =
        'Saving…';
      break;

    case 'unsaved':
      saveStatusText =
        'Unsaved';
      break;

    case 'invalid':
      saveStatusText =
        'Needs correction';
      break;

    case 'error':
      saveStatusText =
        'Autosave failed';
      break;

    default:
      break;
  }

  return (
    <article
      className="clinical-special-test-card"
      style={{
        border:
          '1px solid #ddd',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* ===================================================
          HEADER
          =================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems:
            'flex-start',
          gap: 12,
          flexWrap: 'wrap',
          padding: 10,
          borderBottom:
            '1px solid #e5e5e5',
          background:
            '#fafafa',
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              flexWrap: 'wrap',
            }}
          >
            <strong
              style={{
                fontSize: 15,
              }}
            >
              {row.displayName ||
                row.testCode ||
                'Special Test'}
            </strong>

            {side && (
              <span
                style={{
                  display:
                    'inline-block',
                  borderRadius: 999,
                  padding:
                    '2px 7px',
                  background:
                    '#eef3f8',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {side}
              </span>
            )}

            {row.required && (
              <span
                title={
                  requiredSatisfied
                    ? 'Required special-test result is complete.'
                    : 'Required special-test result is still incomplete.'
                }
                style={{
                  display: 'inline-block',
                  borderRadius: 999,
                  padding: '2px 7px',
                  background: requiredSatisfied
                    ? '#e8f5e9'
                    : '#fce8e6',
                  color: requiredSatisfied
                    ? '#1f6b2a'
                    : '#8a1c13',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {requiredSatisfied
                  ? 'Required ✓'
                  : 'Required'}
              </span>
            )}
          </div>

          {row.testCode && (
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: '#666',
              }}
            >
              {row.testCode}
            </div>
          )}
        </div>

        <div
          aria-live="polite"
          style={{
            fontSize: 12,
            fontWeight: 700,
            color:
              saveStatus ===
              'error'
                ? '#b42318'
                : saveStatus ===
                    'saving'
                  ? '#555'
                  : '#2e6a35',
          }}
        >
          {saveStatusText}
        </div>
      </div>

      <div
        style={{
          padding: 10,
        }}
      >
        {/* =================================================
            CURRENT RESULT
            ================================================= */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'minmax(0, 1fr)',
            gap: 12,
          }}
        >
          <div>
            <label
              htmlFor={`special-test-result-${key}`}
              style={{
                display: 'block',
                marginBottom: 5,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              Test Result

              {row.required && (
                <span
                  aria-hidden="true"
                  style={{
                    marginLeft: 4,
                    color: '#b42318',
                  }}
                >
                  *
                </span>
              )}
            </label>

            <textarea
              id={`special-test-result-${key}`}
              rows={largeResult ? 8 : 2}
              className="clinical-special-test-card__textarea"
              value={
                row.draftTestResult
              }
              disabled={disabled}
              onChange={(event) =>
                onFieldChange(
                  'draftTestResult',
                  event.target.value
                )
              }
              placeholder="Enter special-test result"
              style={{
                width: '100%',
                boxSizing:
                  'border-box',
                resize: 'vertical',
                border:
                  '1px solid #bbb',
                borderRadius: 6,
                padding: 9,
                fontSize: 14,
                lineHeight: 1.45,
                background:
                  disabled
                    ? '#f3f4f6'
                    : '#fff',
              }}
            />
          </div>

        </div>

        <div className="clinical-special-test-card__actions">
          <button type="button" className="clinical-special-test-card__button"
            onClick={() => setLargeResult((value) => !value)}
            aria-pressed={largeResult}>
            {largeResult ? 'Normal Result' : 'Expand Result'}
          </button>
          <button type="button" className="clinical-special-test-card__button"
            onClick={() => setShowComments((value) => !value)}
            aria-expanded={showComments}
            aria-controls={`special-test-comments-panel-${key}`}>
            {showComments ? 'Hide Comments' : 'Comments'}
            {row.draftTestComments ? ' •' : ''}
          </button>
          <button type="button" className="clinical-special-test-card__button"
            onClick={() => setShowPrevious((value) => !value)}
            aria-expanded={showPrevious}
            aria-controls={`special-test-previous-panel-${key}`}>
            {showPrevious ? 'Hide Previous Visit' : 'Previous Visit'}
          </button>
        </div>

        {!showComments && Boolean(row.draftTestComments?.trim()) && (
          <div className="clinical-special-test-card__preview" title={row.draftTestComments}>
            Comments: {row.draftTestComments}
          </div>
        )}

        <div id={`special-test-comments-panel-${key}`}
          hidden={!showComments}>
          <div className="clinical-special-test-card__comment-heading">
            <label htmlFor={`special-test-comments-${key}`}>Comments (optional)</label>
            <button type="button" className="clinical-special-test-card__button"
              onClick={() => setLargeComments((value) => !value)}
              aria-pressed={largeComments}>
              {largeComments ? 'Normal Size' : 'Expand Editor'}
            </button>
          </div>
          <textarea
            id={`special-test-comments-${key}`}
            className="clinical-special-test-card__textarea"
            rows={largeComments ? 8 : 2}
            value={row.draftTestComments}
            disabled={disabled}
            onChange={(event) => onFieldChange('draftTestComments', event.target.value)}
            placeholder="Optional test comments"
          />
        </div>

        <div id={`special-test-previous-panel-${key}`} hidden={!showPrevious}
          className="clinical-special-test-card__previous">
          <PreviousSpecialTest row={row} />
        </div>

        {/* =================================================
            SAVE ERROR
            ================================================= */}
        {saveError && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              border:
                '1px solid #b42318',
              borderRadius: 8,
              background:
                '#fff5f5',
              padding: 11,
            }}
          >
            <strong>
              Special-test result was
              not saved.
            </strong>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
              }}
            >
              {saveError}
            </div>

            {!disabled && (
              <button
                type="button"
                onClick={onRetry}
                style={{
                  marginTop: 8,
                }}
              >
                Retry Save
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * Special Tests editor.
 *
 * Features:
 *
 * - configured tests visible before first save
 * - fixed backend-controlled laterality
 * - separate LEFT / RIGHT rows for bilateral notes
 * - previous-visit comparison
 * - free-text result and comments
 * - serialized autosave
 * - optimistic-lock version refresh
 * - read-only support
 */
export default function ClinicalNoteSpecialTestSection({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator = null,
}) {
  const [
    rows,
    setRows,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    loadError,
    setLoadError,
  ] = useState('');

  const [
    rowStatuses,
    setRowStatuses,
  ] = useState({});

  const [
    rowSaveErrors,
    setRowSaveErrors,
  ] = useState({});

  const [
    editTick,
    setEditTick,
  ] = useState(0);

  const [
    initialized,
    setInitialized,
  ] = useState(false);

  /*
   * Refs used by serialized autosave.
   */
  const rowsRef =
    useRef([]);

  const versionRef =
    useRef(null);

  const rowRevisionRef =
    useRef({});

  const dirtyKeysRef =
    useRef(new Set());

  const failedKeysRef =
    useRef(new Set());

  const savingRef =
    useRef(false);

  const mountedRef =
    useRef(true);

  const flushSaveRef = useRef(null);
  // All edited rows remain unsaved until the backend confirms their revision.
  const unsavedKeysRef = useRef(new Set());
  const inFlightSaveRef = useRef(null);
  const initializedRef = useRef(false);
  const loadErrorRef = useRef('');

  useEffect(() => {
    rowsRef.current =
      rows;
  }, [rows]);

  useEffect(() => {
    return () => {
      mountedRef.current =
        false;
    };
  }, []);

  /**
   * Load:
   *
   * - configured/saved special tests
   * - current parent clinical-note version
   */
  useEffect(() => {
    let cancelled = false;

    async function loadSpecialTests() {
      setLoading(true);
      setLoadError('');
      loadErrorRef.current = '';
      initializedRef.current = false;
      setInitialized(false);
      setRowStatuses({});
      setRowSaveErrors({});

      dirtyKeysRef.current =
        new Set();
      unsavedKeysRef.current = new Set();

      failedKeysRef.current =
        new Set();

      rowRevisionRef.current =
        {};

      savingRef.current =
        false;

      try {
        const [
          specialTestResponse,
          noteResponse,
        ] =
          await Promise.all([
            clinicalNoteService
              .getSpecialTests(
                clinicalNoteId
              ),

            clinicalNoteService
              .getClinicalNote(
                clinicalNoteId
              ),
          ]);

        if (cancelled) {
          return;
        }

        const version =
          resolveEntityVersion(
            noteResponse
          );

        if (
          version === null ||
          version === undefined
        ) {
          throw new Error(
            'The clinical note version could not be loaded.'
          );
        }

        versionRef.current =
          version;

        const normalizedRows =
          sortSpecialTestRows(
            (
              specialTestResponse ||
              []
            ).map(
              normalizeSpecialTestRow
            )
          );

        const statuses =
          {};

        const revisions =
          {};

        normalizedRows.forEach(
          (row) => {
            const key =
              getSpecialTestKey(
                row
              );

            statuses[key] =
              'saved';

            revisions[key] =
              0;
          }
        );

        rowsRef.current =
          normalizedRows;

        rowRevisionRef.current =
          revisions;

        setRows(
          normalizedRows
        );

        setRowStatuses(
          statuses
        );

        initializedRef.current = true;
        setInitialized(true);
      } catch (error) {
        if (cancelled) {
          return;
        }

        loadErrorRef.current = error?.message || 'Special tests could not be loaded.';
        setLoadError(loadErrorRef.current);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (clinicalNoteId) {
      loadSpecialTests();
    } else {
      setLoadError(
        'Clinical note ID is required.'
      );

      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [clinicalNoteId]);

  /**
   * Refresh ClinicalNoteMain.entityVersion after a child save.
   */
  const refreshEntityVersion =
    useCallback(
      async () => {
        const noteResponse =
          await clinicalNoteService
            .getClinicalNote(
              clinicalNoteId
            );

        const newVersion =
          resolveEntityVersion(
            noteResponse
          );

        if (
          newVersion === null ||
          newVersion === undefined
        ) {
          throw new Error(
            'The special test was saved, but the updated clinical note version could not be loaded.'
          );
        }

        versionRef.current =
          newVersion;

        if (
          typeof onVersionChange ===
          'function'
        ) {
          onVersionChange(
            newVersion,
            noteResponse
          );
        }

        return noteResponse;
      },
      [
        clinicalNoteId,
        onVersionChange,
      ]
    );

  /**
   * Update one draft field and mark the row dirty.
   */
  function updateRowField(
    key,
    field,
    value
  ) {
    if (disabled) {
      return;
    }

    const nextRows =
      rowsRef.current.map(
        (row) =>
          getSpecialTestKey(
            row
          ) === key
            ? {
                ...row,
                [field]: value,
              }
            : row
      );

    rowsRef.current =
      nextRows;

    setRows(
      nextRows
    );

    rowRevisionRef.current = {
      ...rowRevisionRef.current,

      [key]:
        (
          rowRevisionRef
            .current[key] ??
          0
        ) + 1,
    };

    /*
     * A new edit is allowed to retry a previously failed row.
     */
    failedKeysRef.current.delete(
      key
    );

    dirtyKeysRef.current.add(
      key
    );
    unsavedKeysRef.current.add(key);

    setRowSaveErrors(
      (current) => ({
        ...current,
        [key]: '',
      })
    );

    const editedRow =
      nextRows.find(
        (row) =>
          getSpecialTestKey(
            row
          ) === key
      );

    const errors =
      editedRow
        ? validateSpecialTestRow(
            editedRow
          )
        : {};

    setRowStatuses(
      (current) => ({
        ...current,

        [key]:
          Object.keys(
            errors
          ).length > 0
            ? 'invalid'
            : 'unsaved',
      })
    );

    setEditTick(
      (current) =>
        current + 1
    );
  }

  /**
   * Merge a successful SpecialTestResponse.
   */
  function applySuccessfulSave(
    key,
    response,
    revisionBeingSaved
  ) {
    const currentRevision =
      rowRevisionRef.current[
        key
      ] ?? 0;

    const preserveDraft =
      currentRevision !==
      revisionBeingSaved;

    const nextRows =
      rowsRef.current.map(
        (row) => {
          if (
            getSpecialTestKey(
              row
            ) !== key
          ) {
            return row;
          }

          return mergeSavedSpecialTestRow(
            row,
            response,
            preserveDraft
          );
        }
      );

    rowsRef.current =
      sortSpecialTestRows(
        nextRows
      );

    setRows(
      rowsRef.current
    );

    if (!preserveDraft) unsavedKeysRef.current.delete(key);

    if (preserveDraft) {
      dirtyKeysRef.current.add(
        key
      );

      setRowStatuses(
        (current) => ({
          ...current,
          [key]:
            'unsaved',
        })
      );
    } else {
      setRowStatuses(
        (current) => ({
          ...current,
          [key]:
            'saved',
        })
      );
    }
  }

  /**
   * Save dirty rows sequentially.
   *
   * Each child save updates the parent note version, so concurrent
   * special-test saves must not use the same expectedVersion.
   */
  const flushSaveQueue =
    useCallback(
      async () => {
        if (
          disabled ||
          !initialized ||
          savingRef.current
        ) {
          return;
        }

        savingRef.current =
          true;

        try {
          while (
            mountedRef.current &&
            dirtyKeysRef.current
              .size > 0
          ) {
            const key =
              Array.from(
                dirtyKeysRef.current
              ).find(
                (candidate) =>
                  !failedKeysRef
                    .current
                    .has(candidate)
              );

            if (!key) {
              break;
            }

            /*
             * Remove while this request is active.
             *
             * Another edit during the request will add the row back.
             */
            dirtyKeysRef.current.delete(
              key
            );

            const row =
              rowsRef.current.find(
                (candidate) =>
                  getSpecialTestKey(
                    candidate
                  ) === key
              );

            if (!row) {
              continue;
            }

            const errors =
              validateSpecialTestRow(
                row
              );

            if (
              Object.keys(
                errors
              ).length > 0
            ) {
              setRowStatuses(
                (current) => ({
                  ...current,
                  [key]:
                    'invalid',
                })
              );

              continue;
            }

            const revisionBeingSaved =
              rowRevisionRef.current[
                key
              ] ?? 0;

            setRowStatuses(
              (current) => ({
                ...current,
                [key]:
                  'saving',
              })
            );

            setRowSaveErrors(
              (current) => ({
                ...current,
                [key]: '',
              })
            );

            try {
              if (
                versionRef.current ===
                  null ||
                versionRef.current ===
                  undefined
              ) {
                await refreshEntityVersion();
              }

              const response =
                await clinicalNoteService
                  .saveSpecialTest(
                    clinicalNoteId,
                    {
                      expectedVersion:
                        versionRef.current,

                      specialTestTemplateId:
                        row
                          .specialTestTemplateId,

                      measurementSide:
                        row
                          .measurementSide,

                      testResult:
                        row
                          .draftTestResult,

                      testComments:
                        row
                          .draftTestComments,
                    }
                  );

              if (
                !mountedRef.current
              ) {
                return;
              }

              /*
               * Refresh the parent optimistic version before another
               * special-test save is allowed.
               */
              const noteResponse =
                await refreshEntityVersion();

              if (
                !mountedRef.current
              ) {
                return;
              }

              applySuccessfulSave(
                key,
                response,
                revisionBeingSaved
              );

              if (
                typeof onSaved ===
                'function'
              ) {
                onSaved(
                  response,
                  noteResponse
                );
              }
            } catch (error) {
              if (
                !mountedRef.current
              ) {
                return;
              }

              /*
               * Prevent an infinite automatic retry loop.
               *
               * A new edit or explicit Retry Save re-enables saving.
               */
              failedKeysRef.current.add(
                key
              );

              dirtyKeysRef.current.delete(
                key
              );

              setRowStatuses(
                (current) => ({
                  ...current,
                  [key]:
                    'error',
                })
              );

              setRowSaveErrors(
                (current) => ({
                  ...current,

                  [key]:
                    error?.message ||
                    'Special-test result could not be autosaved.',
                })
              );
            }
          }
        } finally {
          savingRef.current =
            false;

          const hasRetryableDirtyRows =
            Array.from(
              dirtyKeysRef.current
            ).some(
              (key) =>
                !failedKeysRef
                  .current
                  .has(key)
            );

          if (
            mountedRef.current &&
            hasRetryableDirtyRows
          ) {
            window.setTimeout(
              () => {
                flushSaveRef
                  .current?.();
              },
              150
            );
          }
        }
      },
      [
        clinicalNoteId,
        disabled,
        initialized,
        onSaved,
        refreshEntityVersion,
      ]
    );

  /** Share an awaitable save promise with autosave and the visit navigator. */
  useEffect(() => {
    flushSaveRef.current = () => {
      if (inFlightSaveRef.current) return inFlightSaveRef.current;
      const running = flushSaveQueue();
      const tracked = Promise.resolve(running).finally(() => {
        if (inFlightSaveRef.current === tracked) {
          inFlightSaveRef.current = null;
        }
      });
      inFlightSaveRef.current = tracked;
      return tracked;
    };
  }, [flushSaveQueue]);

  /** Flush all pending results and refuse navigation when anything is unsaved. */
  useEffect(() => {
    if (!navigationCoordinator || !clinicalNoteId) return undefined;
    return navigationCoordinator.register(clinicalNoteId, 'special-tests', {
      flush: async () => {
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
        if (dirtyKeysRef.current.size > 0) {
          await flushSaveRef.current?.();
        }
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
      },
      getState: () => {
        const unsaved = [...unsavedKeysRef.current];
        const invalid = unsaved.some((key) => {
          const row = rowsRef.current.find(item => getSpecialTestKey(item) === key);
          return row && Object.keys(validateSpecialTestRow(row)).length > 0;
        });
        return {
          dirty: unsaved.length > 0 || !initializedRef.current,
          saving: Boolean(inFlightSaveRef.current),
          invalid,
          error: loadErrorRef.current ||
            (!initializedRef.current ? 'Special Tests is still loading.' : '') ||
            (unsaved.some(key => failedKeysRef.current.has(key))
              ? 'A Special Test failed to save. Correct it or select Retry Save before switching visits.'
              : ''),
        };
      },
    });
  }, [navigationCoordinator, clinicalNoteId]);

  /**
   * Debounced autosave.
   */
  useEffect(() => {
    if (
      !initialized ||
      disabled ||
      dirtyKeysRef.current
        .size === 0
    ) {
      return undefined;
    }

    const timer =
      window.setTimeout(
        () => {
          flushSaveRef
            .current?.();
        },
        AUTOSAVE_DELAY_MS
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    editTick,
    initialized,
    disabled,
  ]);

  /**
   * Retry one failed save.
   */
  function retryRow(key) {
    if (disabled) {
      return;
    }

    failedKeysRef.current.delete(
      key
    );

    dirtyKeysRef.current.add(
      key
    );

    setRowSaveErrors(
      (current) => ({
        ...current,
        [key]: '',
      })
    );

    setRowStatuses(
      (current) => ({
        ...current,
        [key]:
          'unsaved',
      })
    );

    setEditTick(
      (current) =>
        current + 1
    );
  }

  /**
   * Overall section autosave state.
   */
  const overallStatus =
    useMemo(() => {
      const values =
        Object.values(
          rowStatuses
        );

      if (
        values.includes(
          'saving'
        )
      ) {
        return 'Saving…';
      }

      if (
        values.includes(
          'error'
        )
      ) {
        return 'Autosave error';
      }

      if (
        values.includes(
          'invalid'
        )
      ) {
        return 'Needs correction';
      }

      if (
        values.includes(
          'unsaved'
        )
      ) {
        return 'Unsaved changes';
      }

      return disabled
        ? 'Read only'
        : 'Saved';
    }, [
      rowStatuses,
      disabled,
    ]);

  if (loading) {
    return (
      <section
        style={{
          border:
            '1px solid #ddd',
          borderRadius: 10,
          padding: 16,
          background: '#fff',
        }}
      >
        Loading special tests…
      </section>
    );
  }

  if (loadError) {
    return (
      <section
        style={{
          border:
            '1px solid #c62828',
          borderRadius: 10,
          padding: 16,
          background:
            '#fff5f5',
        }}
      >
        <strong>
          Special Tests section could
          not be loaded
        </strong>

        <p
          style={{
            marginBottom: 0,
          }}
        >
          {loadError}
        </p>
      </section>
    );
  }

  return (
    <section
      style={{
        border:
          '1px solid #d7d7d7',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* =====================================================
          SECTION HEADER
          ===================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding:
            '13px 16px',
          background:
            '#f7f8fa',
          borderBottom:
            '1px solid #ddd',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 17,
            }}
          >
            Special Tests
          </h3>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              color: '#666',
            }}
          >
            Record findings for each configured test. Open previous results and comments only when needed.
          </div>
        </div>

        <div
          aria-live="polite"
          style={{
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {overallStatus}
        </div>
      </div>

      <div
        style={{
          padding: 10,
        }}
      >
        <div
          style={{
            marginBottom: 10,
            border:
              '1px solid #d9e2ec',
            borderRadius: 8,
            background:
              '#f7fafc',
            padding: 11,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          Test laterality is determined
          by the authorization and
          clinical-note configuration
          and cannot be changed here.

          {' '}

          Required tests may remain blank
          while the note is in Draft;
          complete clinical-note
          validation determines whether
          the note can proceed to review.
        </div>

        {rows.length === 0 && (
          <div
            style={{
              border:
                '1px dashed #bbb',
              borderRadius: 8,
              padding: 16,
              color: '#666',
              background:
                '#fafafa',
            }}
          >
            No special tests are
            configured for this clinical
            note's body part.
          </div>
        )}

        {rows.length > 0 && (
          <div
            style={{
              display: 'grid',
              gap: 14,
            }}
          >
            {rows.map(
              (row) => {
                const key =
                  getSpecialTestKey(
                    row
                  );

                return (
                  <SpecialTestRow
                    key={key}
                    row={row}
                    disabled={
                      disabled
                    }
                    saveStatus={
                      rowStatuses[
                        key
                      ] ||
                      'saved'
                    }
                    saveError={
                      rowSaveErrors[
                        key
                      ] ||
                      ''
                    }
                    onFieldChange={(
                      field,
                      value
                    ) =>
                      updateRowField(
                        key,
                        field,
                        value
                      )
                    }
                    onRetry={() =>
                      retryRow(
                        key
                      )
                    }
                  />
                );
              }
            )}
          </div>
        )}

        {disabled && (
          <div
            style={{
              marginTop: 14,
              fontSize: 13,
              color: '#666',
            }}
          >
            Special-test documentation
            is read only because this
            clinical note is no longer
            in the normal Draft editing
            state.
          </div>
        )}
      </div>
    </section>
  );
}